import React, { useEffect } from 'react';

const MementoPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Memento Pattern - Behavioral Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Memento Pattern - capture and restore object internal state without violating encapsulation. Learn with text editor and game save examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Memento Pattern - capture and restore object internal state without violating encapsulation. Learn with text editor and game save examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'memento pattern, design patterns, behavioral patterns, state capture, undo redo, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'memento pattern, design patterns, behavioral patterns, state capture, undo redo, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Memento Pattern</h1>
        <p style={styles.subtitle}>
          Behavioral Design Pattern - Capture and restore object state
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Memento pattern allows you to capture and externalize an object's internal state 
            so that the object can be restored to this state later, without violating encapsulation. 
            It's commonly used to implement undo/redo functionality.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You need to save and restore the state of an object without exposing its internal 
            structure. Direct access to private fields would violate encapsulation, but you 
            need a way to capture the complete state for later restoration.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Text Editor with Undo/Redo</h2>
          <div style={styles.codeBlock}>
{`// Memento class - stores state
class EditorMemento {
    private final String content;
    private final int cursorPosition;
    private final String fontStyle;
    private final int fontSize;
    private final long timestamp;
    
    public EditorMemento(String content, int cursorPosition, String fontStyle, int fontSize) {
        this.content = content;
        this.cursorPosition = cursorPosition;
        this.fontStyle = fontStyle;
        this.fontSize = fontSize;
        this.timestamp = System.currentTimeMillis();
    }
    
    // Package-private getters (only accessible to Originator)
    String getContent() { return content; }
    int getCursorPosition() { return cursorPosition; }
    String getFontStyle() { return fontStyle; }
    int getFontSize() { return fontSize; }
    long getTimestamp() { return timestamp; }
}

// Originator - creates and uses mementos
class TextEditor {
    private String content;
    private int cursorPosition;
    private String fontStyle;
    private int fontSize;
    
    public TextEditor() {
        this.content = "";
        this.cursorPosition = 0;
        this.fontStyle = "Arial";
        this.fontSize = 12;
    }
    
    // Create memento
    public EditorMemento createMemento() {
        return new EditorMemento(content, cursorPosition, fontStyle, fontSize);
    }
    
    // Restore from memento
    public void restoreFromMemento(EditorMemento memento) {
        this.content = memento.getContent();
        this.cursorPosition = memento.getCursorPosition();
        this.fontStyle = memento.getFontStyle();
        this.fontSize = memento.getFontSize();
    }
    
    // Editor operations
    public void type(String text) {
        String before = content.substring(0, cursorPosition);
        String after = content.substring(cursorPosition);
        content = before + text + after;
        cursorPosition += text.length();
    }
    
    public void delete(int count) {
        if (cursorPosition >= count) {
            String before = content.substring(0, cursorPosition - count);
            String after = content.substring(cursorPosition);
            content = before + after;
            cursorPosition -= count;
        }
    }
    
    public void moveCursor(int position) {
        if (position >= 0 && position <= content.length()) {
            this.cursorPosition = position;
        }
    }
    
    public void setFontStyle(String fontStyle) {
        this.fontStyle = fontStyle;
    }
    
    public void setFontSize(int fontSize) {
        this.fontSize = fontSize;
    }
    
    // Getters for display
    public String getContent() { return content; }
    public int getCursorPosition() { return cursorPosition; }
    public String getFontStyle() { return fontStyle; }
    public int getFontSize() { return fontSize; }
    
    public void display() {
        System.out.println("Content: '" + content + "'");
        System.out.println("Cursor at position: " + cursorPosition);
        System.out.println("Font: " + fontStyle + ", Size: " + fontSize);
        System.out.println("Length: " + content.length() + " characters");
    }
}

// Caretaker - manages mementos
class EditorHistory {
    private Stack<EditorMemento> undoStack;
    private Stack<EditorMemento> redoStack;
    private TextEditor editor;
    
    public EditorHistory(TextEditor editor) {
        this.editor = editor;
        this.undoStack = new Stack<>();
        this.redoStack = new Stack<>();
    }
    
    public void saveState() {
        undoStack.push(editor.createMemento());
        redoStack.clear(); // Clear redo stack when new action is performed
    }
    
    public void undo() {
        if (!undoStack.isEmpty()) {
            // Save current state to redo stack
            redoStack.push(editor.createMemento());
            
            // Restore previous state
            EditorMemento memento = undoStack.pop();
            editor.restoreFromMemento(memento);
            
            System.out.println("Undo performed");
        } else {
            System.out.println("Nothing to undo");
        }
    }
    
    public void redo() {
        if (!redoStack.isEmpty()) {
            // Save current state to undo stack
            undoStack.push(editor.createMemento());
            
            // Restore next state
            EditorMemento memento = redoStack.pop();
            editor.restoreFromMemento(memento);
            
            System.out.println("Redo performed");
        } else {
            System.out.println("Nothing to redo");
        }
    }
    
    public boolean canUndo() {
        return !undoStack.isEmpty();
    }
    
    public boolean canRedo() {
        return !redoStack.isEmpty();
    }
    
    public void showHistory() {
        System.out.println("History: " + undoStack.size() + " undo states, " + 
                          redoStack.size() + " redo states");
    }
}

// Usage
public class MementoDemo {
    public static void main(String[] args) {
        TextEditor editor = new TextEditor();
        EditorHistory history = new EditorHistory(editor);
        
        System.out.println("=== Text Editor with Undo/Redo ===\\n");
        
        // Initial state
        System.out.println("Initial state:");
        editor.display();
        
        // Type some text
        System.out.println("\\n1. Typing 'Hello':");
        history.saveState(); // Save state before action
        editor.type("Hello");
        editor.display();
        
        // Type more text
        System.out.println("\\n2. Typing ' World':");
        history.saveState();
        editor.type(" World");
        editor.display();
        
        // Change font
        System.out.println("\\n3. Changing font to Times New Roman:");
        history.saveState();
        editor.setFontStyle("Times New Roman");
        editor.display();
        
        // Change font size
        System.out.println("\\n4. Changing font size to 14:");
        history.saveState();
        editor.setFontSize(14);
        editor.display();
        
        // Undo operations
        System.out.println("\\n=== Undo Operations ===");
        
        System.out.println("\\nUndo 1 (font size change):");
        history.undo();
        editor.display();
        
        System.out.println("\\nUndo 2 (font style change):");
        history.undo();
        editor.display();
        
        System.out.println("\\nUndo 3 (typing ' World'):");
        history.undo();
        editor.display();
        
        // Redo operations
        System.out.println("\\n=== Redo Operations ===");
        
        System.out.println("\\nRedo 1 (typing ' World'):");
        history.redo();
        editor.display();
        
        System.out.println("\\nRedo 2 (font style change):");
        history.redo();
        editor.display();
        
        // Show history status
        System.out.println();
        history.showHistory();
        
        // Try to undo all the way
        System.out.println("\\n=== Undo All ===");
        while (history.canUndo()) {
            history.undo();
        }
        editor.display();
        
        // Try one more undo (should fail)
        System.out.println();
        history.undo();
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Encapsulation:</strong> Preserves object encapsulation while allowing state access</li>
            <li style={styles.listItem}><strong>Undo/Redo:</strong> Enables implementation of undo/redo functionality</li>
            <li style={styles.listItem}><strong>State History:</strong> Can maintain history of object states</li>
            <li style={styles.listItem}><strong>Rollback:</strong> Allows rollback to previous valid states</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you need to implement undo/redo functionality</li>
            <li style={styles.listItem}>When you need to save and restore object states</li>
            <li style={styles.listItem}>When you want to create checkpoints in your application</li>
            <li style={styles.listItem}>When you need to implement transaction rollback</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Memento pattern is essential for applications that need state management and 
            rollback capabilities. It's commonly used in text editors, games, database 
            transactions, and any application requiring undo/redo functionality.
          </p>
        </section>
      </main>
    </article>
  );
};

export default MementoPattern;
